import React, { useState } from "react";
import { getValuePositionInDistribution, getBucketIndexForValue, formatRatioDisplay } from "./utils";

const DistributionHistogram = ({ distribution, patternMedian, baselineMedian, label, formatValue }) => {
  const [hoveredBucket, setHoveredBucket] = useState(null);

  if (!distribution || distribution.length === 0) return null;

  const rangeStart = distribution[0].start;
  const rangeEnd = distribution[distribution.length - 1].end;

  const logHeights = distribution.map((b) => Math.log(b.count + 1));
  const maxLog = Math.max(...logHeights);
  const minLog = Math.min(...logHeights);
  const logRange = maxLog - minLog || 1;

  const patternPos = getValuePositionInDistribution(patternMedian, distribution);
  const baselinePos = getValuePositionInDistribution(baselineMedian, distribution);
  const ratio = baselineMedian != null && baselineMedian !== 0 && patternMedian != null ? patternMedian / baselineMedian : null;
  const diff = formatRatioDisplay(ratio);
  const isAbove = diff?.above ?? (baselineMedian != null && patternMedian != null && patternMedian >= baselineMedian);
  const patternBucketIndex = getBucketIndexForValue(patternMedian, distribution);
  const baselineBucketIndex = getBucketIndexForValue(baselineMedian, distribution);

  const xStr = patternMedian != null ? (formatValue ? formatValue(patternMedian) : String(patternMedian)) : "—";
  const yStr = baselineMedian != null ? (formatValue ? formatValue(baselineMedian) : String(baselineMedian)) : "—";
  const summaryStr =
    patternMedian == null && label === "Views"
      ? "No views data"
      : patternMedian == null && label === "Engagement %"
        ? "No engagement data"
        : `${xStr} vs ${yStr} baseline`;

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center text-xs text-theme-text opacity-70">
        <span>{label}</span>
        <span className="font-medium text-theme-text text-right max-w-[85%]">
          {summaryStr}
        </span>
      </div>
      <div className="relative h-16">
        {hoveredBucket != null && distribution[hoveredBucket] && (
          <div
            className="absolute z-10 px-2 py-1.5 rounded bg-theme-secondary border border-theme-secondary text-theme-text text-xs shadow-lg pointer-events-none"
            style={{
              left: `${((hoveredBucket + 0.5) / distribution.length) * 100}%`,
              transform: "translate(-50%, -100%)",
              top: 0,
              marginTop: "-4px",
            }}
          >
            <div className="font-medium whitespace-nowrap">
              {distribution[hoveredBucket].open_ended
                ? `${formatValue ? formatValue(distribution[hoveredBucket].start) : distribution[hoveredBucket].start}+`
                : `${formatValue ? formatValue(distribution[hoveredBucket].start) : distribution[hoveredBucket].start} – ${formatValue ? formatValue(distribution[hoveredBucket].end) : distribution[hoveredBucket].end}`}
            </div>
            <div className="text-theme-text opacity-80 mt-0.5">
              {distribution[hoveredBucket].count} video{distribution[hoveredBucket].count !== 1 ? "s" : ""}
            </div>
          </div>
        )}
        <div className="absolute inset-0 flex items-end gap-px">
          {distribution.map((bucket, i) => {
            const logH = Math.log(bucket.count + 1);
            const heightPct = ((logH - minLog) / logRange) * 100;
            const isHovered = hoveredBucket === i;
            const isPatternBucket = patternBucketIndex != null && i === patternBucketIndex;
            const barBg = isPatternBucket
              ? isAbove
                ? "bg-green-500/45 dark:bg-green-400/45"
                : "bg-red-500/45 dark:bg-red-400/45"
              : "bg-theme-text";
            const barOpacity = isHovered ? "opacity-60" : isPatternBucket ? "opacity-40 hover:opacity-50" : "opacity-15 hover:opacity-25";
            return (
              <div
                key={i}
                className={`flex-1 rounded-t-sm cursor-pointer transition-opacity ${barBg} ${barOpacity}`}
                style={{ height: `${Math.max(heightPct, 4)}%` }}
                onMouseEnter={() => setHoveredBucket(i)}
                onMouseLeave={() => setHoveredBucket(null)}
                role="img"
                aria-label={`${bucket.open_ended ? `${formatValue ? formatValue(bucket.start) : bucket.start}+` : `${formatValue ? formatValue(bucket.start) : bucket.start} to ${formatValue ? formatValue(bucket.end) : bucket.end}`}: ${bucket.count} videos`}
              />
            );
          })}
        </div>
        {baselinePos != null && baselinePos >= 0 && baselinePos <= 100 && (
          <div
            className="absolute top-0 bottom-0 w-px border-l border-dashed border-theme-text opacity-40 pointer-events-none"
            style={{ left: `${baselinePos}%` }}
            title={`Baseline median: ${formatValue ? formatValue(baselineMedian) : baselineMedian}`}
          />
        )}
        {patternPos != null && patternPos >= 0 && patternPos <= 100 && (
          <div
            className={`absolute top-0 bottom-0 w-0.5 pointer-events-none ${isAbove ? "bg-green-500 dark:bg-green-400" : "bg-red-500 dark:bg-red-400"}`}
            style={{ left: `${patternPos}%` }}
            title={`Pattern median: ${formatValue ? formatValue(patternMedian) : patternMedian}`}
          />
        )}
      </div>
      <div className="flex justify-between text-xs text-theme-text opacity-50">
        <span>{formatValue ? formatValue(rangeStart) : rangeStart}</span>
        <span>
          {distribution[distribution.length - 1]?.open_ended
            ? `${formatValue ? formatValue(distribution[distribution.length - 1].start) : distribution[distribution.length - 1].start}+`
            : formatValue ? formatValue(rangeEnd) : rangeEnd}
        </span>
      </div>
    </div>
  );
};

export default DistributionHistogram;
